import React, { useEffect, useRef, useState } from "react";
import SeasonPositionalRecordsTable from "./SeasonPositionalRecordsTable";
import SeasonTeamRecordsTable from "./SeasonTeamRecordsTable";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import { safeFetch } from "../utils/api";

const PREFETCH_PROVIDERS = new Set(["yahoo"]);

const LeagueRecordsTab = ({ provider, leagueYear, leagueId, capabilities = {} }) => {
    const [records, setRecords] = useState(null);
    const [prefetch, setPrefetch] = useState(null);
    const [error, setError] = useState(null);
    const cancelled = useRef(false);
    const supportsPositionRecords = (records?.capabilities || capabilities).supports_position_records !== false;

    useEffect(() => {
        cancelled.current = false;
        const loadRecords = () => safeFetch(`/api/season-records/${provider}/${leagueYear}/${leagueId}/`, {}, false, 2)
            .then((data) => { if (!cancelled.current) setRecords(data); })
            .catch(() => { if (!cancelled.current) setError("Records could not be loaded."); });

        if (!PREFETCH_PROVIDERS.has(provider)) {
            loadRecords();
            return () => { cancelled.current = true; };
        }

        safeFetch(`/api/prefetch-season-data/${provider}/${leagueYear}/${leagueId}/`, {}, false, 0)
            .then(async ({ total_weeks, cached_weeks = [] }) => {
                if (!total_weeks) return loadRecords();
                const cached = new Set(cached_weeks);
                let loaded = cached.size;
                setPrefetch({ total: total_weeks, loaded });
                for (let week = 1; week <= total_weeks && !cancelled.current; week += 1) {
                    if (cached.has(week)) continue;
                    try { await safeFetch(`/api/prefetch-season-data/${provider}/${leagueYear}/${leagueId}/${week}/`, {}, false, false, 0); } catch { /* continue */ }
                    loaded += 1;
                    if (!cancelled.current) setPrefetch({ total: total_weeks, loaded });
                }
                if (!cancelled.current) loadRecords();
            })
            .catch(loadRecords);
        return () => { cancelled.current = true; };
    }, [provider, leagueYear, leagueId]);

    if (error) return <p className="rounded-lg border border-coral/30 bg-coral-light px-4 py-3 text-sm font-semibold text-coral">{error}</p>;
    if (!records) return (
        <div className="rounded-xl border border-border bg-surface px-5 py-6 shadow-sm">
            <p className="font-display text-xl font-bold text-forest">Loading season records</p>
            <p className="mt-1 text-sm font-semibold text-text-muted">Records load independently from the rest of the league dashboard.</p>
            {prefetch ? <div className="mt-5 flex gap-1">{Array.from({ length: prefetch.total }, (_, index) => <span key={index} className={`h-2 flex-1 rounded-sm ${index < prefetch.loaded ? "bg-mustard" : "bg-border"}`} />)}</div> : null}
        </div>
    );

    return (
        <div className="space-y-6">
            <CopyableContainer
                title={<div><LeagueTabHeading eyebrow="Season Records" title="Team Records" /><p className="mt-1 text-sm font-semibold text-text-muted">Best and worst season performances.</p></div>}
                fileName="season-team-records"
                className="border-b border-border pb-6 last:border-b-0"
            >
                <div className="mt-5 w-full overflow-x-auto"><SeasonTeamRecordsTable bestTeamStats={records.best_team_stats} worstTeamStats={records.worst_team_stats} /></div>
            </CopyableContainer>
            {supportsPositionRecords ? <CopyableContainer
                title={<div><LeagueTabHeading eyebrow="Season Records" title="Positional Records" /><p className="mt-1 text-sm font-semibold text-text-muted">Top and bottom positional performances.</p></div>}
                fileName="season-positional-records"
                className="border-b border-border pb-6 last:border-b-0"
            >
                <div className="mt-5 w-full overflow-x-auto"><SeasonPositionalRecordsTable bestPositionalStats={records.best_position_stats} worstPositionalStats={records.worst_position_stats} /></div>
            </CopyableContainer> : null}
        </div>
    );
};

export default LeagueRecordsTab;
